import { useState, useMemo } from "react";
import { Brain, RefreshCw, Lock, GitBranch, BarChart2, HelpCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { ccbNoteKey } from "./CCBSkillHint";

const BASE = process.env.REACT_APP_BACKEND_URL;

const THEME_COLORS = ["#314E6E", "#A37B32", "#4E6654", "#7B4E8C", "#4E6E7B", "#8C4E4E"];

interface WeekData {
  week: string;
  count: number;
  excerpts: string[];
}

interface Theme {
  name: string;
  description: string;
  weeks: WeekData[];
}

interface TooltipData {
  theme: string;
  week: string;
  excerpts: string[];
}

interface Props {
  baProjectId: string;
}

const ALL_TABS = [
  "diagram", "stories", "kanban", "timeline", "requirements", "compliance",
  "stakeholders", "decisions", "risks", "meeting-notes", "sprints", "okr",
  "documents", "analysis", "knowledge-base", "handover",
];

export default function BrainTrajectoryPanel({ baProjectId }: Props) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [error, setError] = useState("");
  const [hoveredTheme, setHoveredTheme] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const [activeView, setActiveView] = useState<"timeline" | "graph" | "about">("timeline");
  const [graphHover, setGraphHover] = useState<{ node?: string; edge?: [string, string] } | null>(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  async function analyze() {
    setLoading(true);
    setError("");
    try {
      const [decisionsRes, risksRes, notesRes, sprintsRes, objRes] = await Promise.allSettled([
        axios.get(`${BASE}/api/tools/decisions`, { params: { project_id: baProjectId }, headers }),
        axios.get(`${BASE}/api/tools/risks`, { params: { project_id: baProjectId }, headers }),
        axios.get(`${BASE}/api/tools/meeting-notes`, { params: { project_id: baProjectId }, headers }),
        axios.get(`${BASE}/api/tools/sprints`, { params: { project_id: baProjectId }, headers }),
        axios.get(`${BASE}/api/tools/objectives`, { params: { project_id: baProjectId }, headers }),
      ]);

      const entries: Array<{ content: string; timestamp: string; source: string }> = [];

      const add = (res: PromiseSettledResult<any>, source: string, key: string, dateKey: string) => {
        if (res.status === "fulfilled") {
          (res.value.data || []).forEach((item: any) => {
            if (item[key]) entries.push({ content: item[key], timestamp: item[dateKey] || new Date().toISOString(), source });
          });
        }
      };

      add(decisionsRes, "Decision", "title", "created_at");
      if (decisionsRes.status === "fulfilled") {
        (decisionsRes.value.data || []).forEach((item: any) => {
          if (item.rationale) entries.push({ content: item.rationale, timestamp: item.created_at || new Date().toISOString(), source: "Decision Rationale" });
        });
      }
      add(risksRes, "Risk", "title", "created_at");
      if (risksRes.status === "fulfilled") {
        (risksRes.value.data || []).forEach((item: any) => {
          if (item.mitigation) entries.push({ content: item.mitigation, timestamp: item.created_at || new Date().toISOString(), source: "Risk Mitigation" });
        });
      }
      add(notesRes, "Meeting", "content", "meeting_date");
      add(sprintsRes, "Sprint", "goal", "start_date");
      add(objRes, "OKR", "title", "created_at");

      // Skill Lens notes from localStorage
      ALL_TABS.forEach(tab => {
        const raw = localStorage.getItem(ccbNoteKey(baProjectId, tab));
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            if (parsed.notes) entries.push({ content: parsed.notes, timestamp: parsed.savedAt, source: `Note (${tab})` });
          } catch {}
        }
      });

      if (entries.length === 0) {
        setError("No data yet. Add decisions, risks, meeting notes, or Skill Lens notes first.");
        setLoading(false);
        return;
      }

      const { data } = await axios.post(`${BASE}/api/tools/brain-trajectory`, { entries }, { headers });
      setThemes(data.themes || []);
    } catch {
      setError("Analysis failed. Try again.");
    }
    setLoading(false);
  }

  const allWeeks = useMemo(() => {
    const weeks = new Set<string>();
    themes.forEach(t => t.weeks.forEach(w => weeks.add(w.week)));
    return Array.from(weeks).sort();
  }, [themes]);

  const maxCount = useMemo(() => {
    let m = 1;
    themes.forEach(t => t.weeks.forEach(w => { if (w.count > m) m = w.count; }));
    return m;
  }, [themes]);

  /* ─── Graph view: co-occurrence ─── */
  const coEdges = useMemo(() => {
    const edges: { a: number; b: number; sharedWeeks: string[]; weight: number }[] = [];
    for (let i = 0; i < themes.length; i++) {
      const weeksA = new Set(themes[i].weeks.map((w) => w.week));
      for (let j = i + 1; j < themes.length; j++) {
        const shared = themes[j].weeks.filter((w) => weeksA.has(w.week)).map((w) => w.week);
        if (shared.length > 0) edges.push({ a: i, b: j, sharedWeeks: shared, weight: shared.length });
      }
    }
    return edges;
  }, [themes]);

  const graphNodes = useMemo(() => {
    const GW = 520, GH = 520;
    const cx = GW / 2, cy = GH / 2;
    const r = Math.min(cx, cy) - 70;
    return themes.map((theme, i) => {
      const angle = (2 * Math.PI * i) / themes.length - Math.PI / 2;
      const totalCount = theme.weeks.reduce((s, w) => s + w.count, 0);
      const nodeR = 18 + Math.min(totalCount * 3, 22);
      return {
        i,
        x: cx + r * Math.cos(angle),
        y: cy + r * Math.sin(angle),
        r: nodeR,
        name: theme.name,
        totalCount,
        color: THEME_COLORS[i % THEME_COLORS.length],
      };
    });
  }, [themes]);

  const SVG_W = 860;
  const ROW_H = 68;
  const PAD_LEFT = 170;
  const PAD_TOP = 44;
  const PAD_RIGHT = 40;
  const colW = allWeeks.length > 1 ? (SVG_W - PAD_LEFT - PAD_RIGHT) / (allWeeks.length - 1) : 300;
  const SVG_H = PAD_TOP + themes.length * ROW_H + 36;

  const weekX = (week: string) => PAD_LEFT + allWeeks.indexOf(week) * colW;

  return (
    <div className="flex flex-col h-full overflow-auto" style={{ backgroundColor: "#FCFBF8" }}>

      {/* Privacy callout */}
      <div className="mx-6 mt-6 mb-4 px-4 py-3 rounded-xl flex items-start gap-3"
        style={{ backgroundColor: "#F0ECE5", border: "1px solid #D9D4CC" }}>
        <Lock className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#314E6E" }} />
        <p className="text-[13px] leading-relaxed" style={{ color: "#1E1E1C" }}>
          <strong>This view is yours alone.</strong> What you see here reflects where your thinking has drifted — not a verdict, not a report. You decide what to surface, what to investigate, and what to leave here.
        </p>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-6 mb-5">
        <div className="flex items-center gap-2.5">
          <Brain className="h-5 w-5" style={{ color: "#314E6E" }} />
          <h2 className="font-semibold text-lg" style={{ color: "#1E1E1C", fontFamily: "var(--font-display)" }}>Mind Graph</h2>
          <span className="text-[10px] font-mono px-2 py-0.5 rounded" style={{ backgroundColor: "#314E6E22", color: "#314E6E" }}>BETA</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center border rounded-md overflow-hidden" style={{ borderColor: "#D9D4CC" }}>
            {([
              { id: "timeline", label: "Timeline", icon: <BarChart2 className="h-3 w-3" />, always: false },
              { id: "graph",    label: "Graph",    icon: <GitBranch className="h-3 w-3" />, always: false },
              { id: "about",    label: "vs Obsidian", icon: <HelpCircle className="h-3 w-3" />, always: true },
            ] as const).map(v => (
              (!v.always && themes.length === 0) ? null : (
                <button
                  key={v.id}
                  onClick={() => setActiveView(v.id)}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-mono transition-colors"
                  style={{
                    backgroundColor: activeView === v.id ? "#314E6E" : "transparent",
                    color: activeView === v.id ? "#F7F4EE" : "#5C5C57",
                  }}
                >
                  {v.icon} {v.label}
                </button>
              )
            ))}
          </div>
          <button onClick={analyze} disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium transition-opacity disabled:opacity-50"
            style={{ backgroundColor: "#314E6E", color: "#F7F4EE" }}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Analyzing…" : themes.length > 0 ? "Re-analyze" : "Analyze my thinking"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-6 mb-4 px-4 py-3 rounded-xl text-[13px]"
          style={{ backgroundColor: "#FEF2F2", color: "#991B1B", border: "1px solid #FCA5A5" }}>
          {error}
        </div>
      )}

      {themes.length > 0 && (
        <div className="px-6 pb-8">

          {/* Legend */}
          <div className="flex flex-wrap gap-4 mb-5">
            {themes.map((theme, i) => (
              <div key={theme.name} className="flex items-center gap-1.5 cursor-default"
                onMouseEnter={() => setHoveredTheme(theme.name)}
                onMouseLeave={() => setHoveredTheme(null)}>
                <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: THEME_COLORS[i % THEME_COLORS.length] }} />
                <span className="text-[12px] font-medium" style={{ color: "#1E1E1C" }}>{theme.name}</span>
              </div>
            ))}
          </div>

          {/* ── vs Obsidian view (when themes loaded) ── */}
          {activeView === "about" && <AboutObsidianView />}

          {/* ── Graph view ── */}
          {activeView === "graph" && (
            <div className="rounded-xl border mb-5 flex flex-col items-center py-6" style={{ borderColor: "#D9D4CC", backgroundColor: "#FCFBF8" }}>
              {coEdges.length === 0 ? (
                <p className="text-[13px]" style={{ color: "#9B978F" }}>No co-occurring themes detected — themes appear in separate weeks.</p>
              ) : (
                <>
                  <p className="text-[11px] font-mono mb-4" style={{ color: "#9B978F" }}>
                    Edges = themes that surface in the same week · Thickness = frequency of overlap
                  </p>
                  <svg width={520} height={520} style={{ display: "block" }}>
                    <defs>
                      {graphNodes.map(n => (
                        <radialGradient key={n.i} id={`ng-${n.i}`} cx="40%" cy="35%" r="60%">
                          <stop offset="0%" stopColor={n.color} stopOpacity="0.25" />
                          <stop offset="100%" stopColor={n.color} stopOpacity="0.08" />
                        </radialGradient>
                      ))}
                    </defs>

                    {/* Edges */}
                    {coEdges.map((e) => {
                      const a = graphNodes[e.a];
                      const b = graphNodes[e.b];
                      const edgeKey = `${e.a}-${e.b}`;
                      const isHovered = graphHover?.edge?.[0] === a.name || graphHover?.edge?.[1] === b.name
                        || graphHover?.node === a.name || graphHover?.node === b.name;
                      const maxW = Math.max(...coEdges.map(x => x.weight));
                      const sw = 1.5 + (e.weight / maxW) * 4;
                      const midX = (a.x + b.x) / 2 + (b.y - a.y) * 0.15;
                      const midY = (a.y + b.y) / 2 - (b.x - a.x) * 0.15;
                      return (
                        <g key={edgeKey}
                          onMouseEnter={() => setGraphHover({ edge: [a.name, b.name] })}
                          onMouseLeave={() => setGraphHover(null)}
                          style={{ cursor: "default" }}>
                          <path
                            d={`M ${a.x} ${a.y} Q ${midX} ${midY} ${b.x} ${b.y}`}
                            fill="none"
                            stroke={isHovered ? "#A37B32" : "#C8C3BB"}
                            strokeWidth={isHovered ? sw + 1 : sw}
                            strokeOpacity={isHovered ? 0.9 : 0.5}
                            style={{ transition: "stroke 0.15s, stroke-width 0.15s" }}
                          />
                          {isHovered && (
                            <text
                              x={midX} y={midY - 6}
                              textAnchor="middle" fontSize="10"
                              fontFamily="var(--font-mono)" fill="#A37B32">
                              {e.weight}w overlap
                            </text>
                          )}
                        </g>
                      );
                    })}

                    {/* Nodes */}
                    {graphNodes.map((n) => {
                      const isHovered = graphHover?.node === n.name
                        || graphHover?.edge?.[0] === n.name || graphHover?.edge?.[1] === n.name;
                      return (
                        <g key={n.i}
                          onMouseEnter={() => setGraphHover({ node: n.name })}
                          onMouseLeave={() => setGraphHover(null)}
                          style={{ cursor: "default" }}>
                          <circle cx={n.x} cy={n.y} r={n.r + (isHovered ? 3 : 0)}
                            fill={`url(#ng-${n.i})`}
                            stroke={n.color}
                            strokeWidth={isHovered ? 2.5 : 1.5}
                            style={{ transition: "all 0.15s" }}
                          />
                          <text x={n.x} y={n.y - 3} textAnchor="middle"
                            fontSize="11" fontFamily="var(--font-body)"
                            fontWeight="600" fill={n.color}>
                            {n.name.length > 16 ? n.name.slice(0, 14) + "…" : n.name}
                          </text>
                          <text x={n.x} y={n.y + 12} textAnchor="middle"
                            fontSize="9" fontFamily="var(--font-mono)" fill="#9B978F">
                            {n.totalCount} entries
                          </text>
                        </g>
                      );
                    })}
                  </svg>

                  {/* Hover detail */}
                  {graphHover?.node && (() => {
                    const th = themes.find(t => t.name === graphHover.node);
                    return th ? (
                      <div className="mt-4 px-4 py-3 rounded-xl border max-w-sm" style={{ borderColor: "#D9D4CC", backgroundColor: "#F7F4EE" }}>
                        <p className="font-semibold text-[13px] mb-1" style={{ color: "#1E1E1C" }}>{th.name}</p>
                        <p className="text-[12px]" style={{ color: "#5C5C57" }}>{th.description}</p>
                      </div>
                    ) : null;
                  })()}
                  {graphHover?.edge && (
                    <div className="mt-4 px-4 py-3 rounded-xl border max-w-sm text-center" style={{ borderColor: "#D9D4CC", backgroundColor: "#F7F4EE" }}>
                      <p className="text-[12px]" style={{ color: "#5C5C57" }}>
                        <strong style={{ color: "#1E1E1C" }}>{graphHover.edge[0]}</strong> and <strong style={{ color: "#1E1E1C" }}>{graphHover.edge[1]}</strong> surface together
                      </p>
                      <p className="text-[11px] font-mono mt-1" style={{ color: "#A37B32" }}>
                        {coEdges.find(e => graphNodes[e.a].name === graphHover.edge![0] && graphNodes[e.b].name === graphHover.edge![1])?.sharedWeeks.join(", ")}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Bubble timeline SVG */}
          {activeView === "timeline" && <div className="overflow-x-auto rounded-xl border mb-5" style={{ borderColor: "#D9D4CC", backgroundColor: "#FCFBF8" }}>
            <svg width={SVG_W} height={SVG_H} style={{ display: "block", minWidth: SVG_W }}>

              {/* Week labels */}
              {allWeeks.map(week => (
                <text key={week} x={weekX(week)} y={PAD_TOP - 12}
                  textAnchor="middle" fontSize="10" fontFamily="var(--font-mono)" fill="#9B978F">
                  {week.replace(/^\d{4}-/, "")}
                </text>
              ))}

              {/* Vertical grid */}
              {allWeeks.map(week => (
                <line key={week} x1={weekX(week)} y1={PAD_TOP - 4} x2={weekX(week)} y2={SVG_H - 16}
                  stroke="#EDE9E0" strokeWidth="1" />
              ))}

              {/* Themes */}
              {themes.map((theme, ti) => {
                const color = THEME_COLORS[ti % THEME_COLORS.length];
                const y = PAD_TOP + ti * ROW_H + ROW_H / 2;
                const dimmed = hoveredTheme !== null && hoveredTheme !== theme.name;

                return (
                  <g key={theme.name} style={{ opacity: dimmed ? 0.2 : 1, transition: "opacity 0.2s" }}>
                    {/* Label */}
                    <text x={PAD_LEFT - 14} y={y + 4} textAnchor="end"
                      fontSize="12" fontFamily="var(--font-body)" fill="#1E1E1C" fontWeight="500">
                      {theme.name.length > 23 ? theme.name.slice(0, 21) + "…" : theme.name}
                    </text>

                    {/* Baseline */}
                    <line x1={PAD_LEFT} y1={y} x2={SVG_W - PAD_RIGHT} y2={y}
                      stroke="#EDE9E0" strokeWidth="1" strokeDasharray="3 5" />

                    {/* Trend line */}
                    {theme.weeks.length > 1 && (
                      <polyline
                        points={theme.weeks.map(w => `${weekX(w.week)},${y}`).join(" ")}
                        fill="none" stroke={color} strokeWidth="1.5" strokeOpacity="0.35"
                      />
                    )}

                    {/* Bubbles */}
                    {theme.weeks.map(w => {
                      const r = 7 + (w.count / maxCount) * 13;
                      return (
                        <g key={w.week} style={{ cursor: "pointer" }}
                          onMouseEnter={() => setTooltip({ theme: theme.name, week: w.week, excerpts: w.excerpts })}
                          onMouseLeave={() => setTooltip(null)}>
                          <circle cx={weekX(w.week)} cy={y} r={r}
                            fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" />
                          <text x={weekX(w.week)} y={y + 4} textAnchor="middle"
                            fontSize="10" fontFamily="var(--font-mono)" fill={color} fontWeight="700">
                            {w.count}
                          </text>
                        </g>
                      );
                    })}
                  </g>
                );
              })}
            </svg>
          </div>}

          {/* Hover tooltip — timeline view only */}
          {activeView === "timeline" && tooltip && (
            <div className="mb-5 px-4 py-3 rounded-xl border" style={{ borderColor: "#D9D4CC", backgroundColor: "#F7F4EE" }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-[13px]" style={{ color: "#1E1E1C" }}>{tooltip.theme}</span>
                <span className="font-mono text-[11px]" style={{ color: "#9B978F" }}>{tooltip.week}</span>
              </div>
              <ul className="space-y-1">
                {tooltip.excerpts.map((e, i) => (
                  <li key={i} className="text-[12px] leading-relaxed" style={{ color: "#5C5C57" }}>· {e}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Theme cards */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {themes.map((theme, i) => (
              <div key={theme.name} className="p-3.5 rounded-xl border"
                style={{
                  borderColor: "#D9D4CC",
                  borderTop: `3px solid ${THEME_COLORS[i % THEME_COLORS.length]}`,
                  backgroundColor: "#FCFBF8",
                }}>
                <p className="font-semibold text-[13px] mb-1" style={{ color: "#1E1E1C" }}>{theme.name}</p>
                <p className="text-[12px] leading-relaxed" style={{ color: "#5C5C57" }}>{theme.description}</p>
                <p className="font-mono text-[10px] mt-2" style={{ color: "#9B978F" }}>
                  {theme.weeks.reduce((s, w) => s + w.count, 0)} entries across {theme.weeks.length} week{theme.weeks.length !== 1 ? "s" : ""}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeView === "about" && themes.length === 0 && (
        <AboutObsidianView />
      )}

      {!loading && themes.length === 0 && !error && activeView !== "about" && (
        <div className="flex flex-col items-center justify-center flex-1 gap-4 py-24">
          <Brain className="h-12 w-12" style={{ color: "#D9D4CC" }} />
          <p className="text-[14px] text-center max-w-xs leading-relaxed" style={{ color: "#9B978F" }}>
            Hit "Analyze my thinking" to see what themes are surfacing across your decisions, risks, meeting notes, and reflections.
          </p>
        </div>
      )}
    </div>
  );
}

const COMPARISONS = [
  {
    label: "Source of knowledge",
    obsidian: "Notes you write manually — you decide what goes in.",
    here: "Your BA work artifacts: decisions, risks, meeting notes, OKRs. No extra effort required.",
  },
  {
    label: "How links form",
    obsidian: "You create [[wikilinks]] by hand. The graph reflects your intentional connections.",
    here: "AI extracts themes and finds co-occurrences automatically from what you actually wrote.",
  },
  {
    label: "What drifts over time",
    obsidian: "Your note volume and folder structure — shows what you explored.",
    here: "Your cognitive focus as a BA — which problems kept surfacing even when you weren't aware of them.",
  },
  {
    label: "Who it's for",
    obsidian: "Anyone building a personal knowledge base — writers, researchers, students.",
    here: "Business Analysts. Themes it finds are BA-native: scope creep, stakeholder trust, technical trade-offs.",
  },
  {
    label: "Transparency",
    obsidian: "Your vault is yours — but a general repository you might share or publish.",
    here: "Explicitly private by design. Sharpens your judgement before stakeholder conversations.",
  },
  {
    label: "Output",
    obsidian: "A graph you browse — useful for rediscovering connections made months ago.",
    here: "A trajectory — useful for seeing where attention is drifting right now, before the project drifts with it.",
  },
];

function AboutObsidianView() {
  return (
    <div className="px-6 pb-8 space-y-6">
      <div>
        <h3 className="font-semibold text-base mb-1" style={{ color: "#1E1E1C", fontFamily: "var(--font-display)" }}>
          How Mind Graph differs from Obsidian's Second Brain
        </h3>
        <p className="text-[13px] leading-relaxed" style={{ color: "#5C5C57" }}>
          Both visualise how knowledge connects. But the intent, source, and audience are different.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {COMPARISONS.map(row => (
          <div key={row.label} className="p-4 rounded-xl border" style={{ borderColor: "#D9D4CC", backgroundColor: "#F7F4EE" }}>
            <p className="text-[11px] font-mono uppercase tracking-widest mb-3" style={{ color: "#A37B32" }}>{row.label}</p>
            <div className="space-y-2.5">
              <div className="flex gap-2">
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0 mt-0.5" style={{ backgroundColor: "#E8E4DC", color: "#5C5C57" }}>Obsidian</span>
                <p className="text-[12px] leading-relaxed" style={{ color: "#5C5C57" }}>{row.obsidian}</p>
              </div>
              <div className="flex gap-2">
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0 mt-0.5" style={{ backgroundColor: "#314E6E22", color: "#314E6E" }}>Here</span>
                <p className="text-[12px] leading-relaxed" style={{ color: "#1E1E1C" }}>{row.here}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="px-4 py-3 rounded-xl" style={{ backgroundColor: "#F0ECE5", border: "1px solid #D9D4CC" }}>
        <p className="text-[13px] leading-relaxed italic" style={{ color: "#5C5C57" }}>
          Obsidian shows you what you <em>chose to remember</em>. Mind Graph shows you what you <em>couldn't stop thinking about</em> — even when you didn't realise it.
        </p>
      </div>
    </div>
  );
}
